import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Target, Plus, History, Heart, User, LogOut, FileText, Bookmark, ChevronDown, ExternalLink, Zap, TrendingUp, BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [skill2incomeHistory, setSkill2incomeHistory] = useState([]);
  const [savedPaths, setSavedPaths] = useState([]);
  const [favoriteFAQs, setFavoriteFAQs] = useState([]);
  const [expandedFavFAQ, setExpandedFavFAQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('assessments');
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (user && token) {
      fetchData();
    }
  }, [user, token]);

  const fetchData = async () => {
    try {
      const [assessmentsRes, skill2incomeRes, savedPathsRes, favoritesRes, usageRes] = await Promise.all([
        axios.get(`${API}/assessments`),
        axios.get(`${API}/skill2income`),
        axios.get(`${API}/saved-paths`),
        axios.get(`${API}/favorites`),
        axios.get(`${API}/usage`)
      ]);
      setAssessments(assessmentsRes.data);
      setSkill2incomeHistory(skill2incomeRes.data);
      setSavedPaths(savedPathsRes.data);
      setFavoriteFAQs(favoritesRes.data);
      setUsage(usageRes.data);
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (faqId) => {
    try {
      await axios.delete(`${API}/favorites/${faqId}`);
      setFavoriteFAQs(prev => prev.filter(f => f.faq_id !== faqId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error('Failed to remove favorite');
    }
  };

  const handleDeleteSavedPath = async (pathId) => {
    try {
      await axios.delete(`${API}/saved-paths/${pathId}`);
      toast.success('Path removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove path');
      console.error(error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="glass-nav sticky top-0 z-50 px-4 md:px-8 lg:px-12 py-4 border-b border-earth-200">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-earth-700">O4S.ai</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4" />
              <span className="font-medium">{user?.name}</span>
            </div>
            <Button
              data-testid="logout-btn"
              variant="ghost"
              onClick={handleLogout}
              className="gap-2"
            >
              <LogOut className="w-4 h-4" /> Logout
            </Button>
          </div>
        </div>
      </nav>

      {/* Dashboard Content */}
      <div className="px-4 md:px-8 lg:px-12 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Banner */}
          <div className="bg-secondary rounded-2xl p-8 mb-12 text-white">
            <h2 className="text-4xl font-heading font-bold mb-2">Welcome back, {user?.name}!</h2>
            <p className="text-xl mb-6 text-white/80">Ready to explore more career opportunities?</p>
            <Button
              data-testid="start-new-assessment-btn"
              onClick={() => navigate('/assessment')}
              className="bg-white text-secondary hover:bg-earth-100 h-12 px-8 font-semibold rounded-xl gap-2 btn-press"
            >
              <Plus className="w-5 h-5" /> Start New Assessment
            </Button>
          </div>

          {/* Daily Free Tier Banner */}
          {usage && (
            <div data-testid="daily-usage-banner" className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className={`rounded-xl p-5 border cursor-pointer transition-all hover:shadow-md ${usage.assessment.used < usage.assessment.limit ? 'bg-green-50 border-green-200' : 'bg-earth-100 border-earth-200'}`} onClick={() => navigate('/assessment')}>
                <div className="flex items-center gap-3 mb-2">
                  <Zap className={`w-5 h-5 ${usage.assessment.used < usage.assessment.limit ? 'text-green-600' : 'text-earth-400'}`} />
                  <span className="font-semibold text-sm text-earth-700">Career Assessment</span>
                </div>
                <p className="text-2xl font-bold text-earth-700">{usage.assessment.limit - usage.assessment.used} <span className="text-sm font-normal text-earth-500">/ {usage.assessment.limit} free today</span></p>
                {usage.assessment.used < usage.assessment.limit ? (
                  <button className="text-xs text-green-700 font-medium mt-1 hover:underline">Start Assessment &rarr;</button>
                ) : (
                  <p className="text-xs text-earth-500 mt-1">Resets tomorrow. Try Ask O4S.AI for unlimited guidance!</p>
                )}
              </div>
              <div className={`rounded-xl p-5 border cursor-pointer transition-all hover:shadow-md ${usage.skill2income.used < usage.skill2income.limit ? 'bg-green-50 border-green-200' : 'bg-earth-100 border-earth-200'}`} onClick={() => navigate('/skill2income')}>
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className={`w-5 h-5 ${usage.skill2income.used < usage.skill2income.limit ? 'text-green-600' : 'text-earth-400'}`} />
                  <span className="font-semibold text-sm text-earth-700">Skill2Income</span>
                </div>
                <p className="text-2xl font-bold text-earth-700">{usage.skill2income.limit - usage.skill2income.used} <span className="text-sm font-normal text-earth-500">/ {usage.skill2income.limit} free today</span></p>
                {usage.skill2income.used < usage.skill2income.limit ? (
                  <button className="text-xs text-green-700 font-medium mt-1 hover:underline">Explore Skill2Income &rarr;</button>
                ) : (
                  <p className="text-xs text-earth-500 mt-1">Resets tomorrow. Try Ask O4S.AI for unlimited guidance!</p>
                )}
              </div>
              <div className="rounded-xl p-5 border bg-primary/5 border-primary/20">
                <div className="flex items-center gap-3 mb-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  <span className="font-semibold text-sm text-earth-700">Ask O4S.AI</span>
                </div>
                <p className="text-2xl font-bold text-primary">Unlimited</p>
                <button onClick={() => navigate('/faqs')} className="text-xs text-primary font-medium mt-1 hover:underline">Browse curated career content &rarr;</button>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-4 mb-8 border-b border-earth-200">
            <button
              data-testid="tab-assessments"
              onClick={() => setActiveTab('assessments')}
              className={`pb-4 px-4 font-heading font-semibold transition-colors ${
                activeTab === 'assessments'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-earth-500 hover:text-earth-700'
              }`}
            >
              <History className="w-5 h-5 inline mr-2" />
              My Assessments ({assessments.length})
            </button>
            <button
              data-testid="tab-skill2income"
              onClick={() => setActiveTab('skill2income')}
              className={`pb-4 px-4 font-heading font-semibold transition-colors ${
                activeTab === 'skill2income'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-earth-500 hover:text-earth-700'
              }`}
            >
              <TrendingUp className="w-5 h-5 inline mr-2" />
              Skill2Income ({skill2incomeHistory.filter(s => s.status === 'done').length})
            </button>
            <button
              data-testid="tab-saved"
              onClick={() => setActiveTab('saved')}
              className={`pb-4 px-4 font-heading font-semibold transition-colors ${
                activeTab === 'saved'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-earth-500 hover:text-earth-700'
              }`}
            >
              <Heart className="w-5 h-5 inline mr-2" />
              Saved Paths ({savedPaths.length})
            </button>
            <button
              data-testid="tab-favorites"
              onClick={() => setActiveTab('favorites')}
              className={`pb-4 px-4 font-heading font-semibold transition-colors ${
                activeTab === 'favorites'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Bookmark className="w-5 h-5 inline mr-2" />
              Favorite FAQs ({favoriteFAQs.length})
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Assessments Tab */}
              {activeTab === 'assessments' && (
                <div className="grid gap-6">
                  {assessments.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-2xl">
                      <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-600 mb-4">No assessments yet</p>
                      <Button
                        data-testid="start-first-assessment-btn"
                        onClick={() => navigate('/assessment')}
                        className="btn-primary"
                      >
                        Start Your First Assessment
                      </Button>
                    </div>
                  ) : (
                    assessments.map((assessment) => (
                      <div
                        key={assessment.id}
                        data-testid={`assessment-card-${assessment.id}`}
                        className="bg-white rounded-2xl shadow-lg p-6 hover-lift cursor-pointer"
                        onClick={() => navigate(`/results/${assessment.id}`)}
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <p className="text-sm text-slate-500 mb-1">
                              {new Date(assessment.created_at).toLocaleDateString('en-IN', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                            <h4 className="text-xl font-heading font-bold">{assessment.data.education_level}</h4>
                            <p className="text-slate-600">{assessment.data.board_stream}</p>
                          </div>
                          <div className="bg-indigo-100 text-primary px-4 py-2 rounded-full font-semibold">
                            {assessment.career_paths.length} Paths
                          </div>
                        </div>
                        <div className="flex gap-2 mb-3">
                          {assessment.data.interests.slice(0, 3).map((interest, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm"
                            >
                              {interest}
                            </span>
                          ))}
                          {assessment.data.interests.length > 3 && (
                            <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                              +{assessment.data.interests.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Skill2Income Tab */}
              {activeTab === 'skill2income' && (
                <div className="grid gap-6">
                  {skill2incomeHistory.filter(s => s.status === 'done').length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-2xl">
                      <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-600 mb-4">No income analyses yet</p>
                      <Button
                        data-testid="start-first-skill2income-btn"
                        onClick={() => navigate('/skill2income')}
                        className="btn-primary"
                      >
                        Start Your First Analysis
                      </Button>
                    </div>
                  ) : (
                    skill2incomeHistory.filter(s => s.status === 'done').map((analysis) => (
                      <div
                        key={analysis.id}
                        data-testid={`skill2income-card-${analysis.id}`}
                        className="bg-white rounded-2xl shadow-lg p-6 hover-lift cursor-pointer"
                        onClick={() => navigate(`/skill2income?id=${analysis.id}`)}
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <p className="text-sm text-slate-500 mb-1">
                              {new Date(analysis.created_at).toLocaleDateString('en-IN', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                            <h4 className="text-xl font-heading font-bold">{analysis.input?.education || 'Income Analysis'}</h4>
                            <p className="text-slate-600">Target: {analysis.input?.target_income} | {analysis.input?.work_preference}</p>
                          </div>
                          <div className="bg-green-100 text-green-700 px-4 py-2 rounded-full font-semibold text-sm">
                            {analysis.result?.earning_paths?.length || 0} Paths
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {analysis.input?.current_skills?.slice(0, 4).map((skill, idx) => (
                            <span key={idx} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                              {skill}
                            </span>
                          ))}
                          {(analysis.input?.current_skills?.length || 0) > 4 && (
                            <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                              +{analysis.input.current_skills.length - 4} more
                            </span>
                          )}
                        </div>
                        {analysis.result?.income_roadmap?.phases && (
                          <div className="flex gap-2 flex-wrap">
                            {analysis.result.income_roadmap.phases.slice(0, 3).map((phase, idx) => (
                              <span key={idx} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                                {phase.title || `Phase ${phase.phase}`}: {phase.income_range}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Saved Paths Tab */}
              {activeTab === 'saved' && (
                <div className="grid gap-6">
                  {savedPaths.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-2xl">
                      <Heart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-600 mb-4">No saved paths yet</p>
                      <Button
                        data-testid="explore-careers-btn"
                        onClick={() => navigate('/assessment')}
                        className="btn-primary"
                      >
                        Explore Career Paths
                      </Button>
                    </div>
                  ) : (
                    savedPaths.map((savedPath) => (
                      <div
                        key={savedPath.id}
                        data-testid={`saved-path-card-${savedPath.id}`}
                        className="bg-white rounded-2xl shadow-lg p-6"
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <h4 className="text-2xl font-heading font-bold mb-2">
                              {savedPath.career_path.title}
                            </h4>
                            <p className="text-slate-600 mb-4">{savedPath.career_path.description}</p>
                            <div className="flex gap-4">
                              <span className="text-sm text-slate-600">
                                Success Rate: <span className="font-bold text-green-600">{savedPath.career_path.success_rate}%</span>
                              </span>
                              <span className="text-sm text-slate-600">
                                Timeline: <span className="font-bold">{savedPath.career_path.timeline}</span>
                              </span>
                              <span className="text-sm text-slate-600">
                                Salary: <span className="font-bold">{savedPath.career_path.salary_range}</span>
                              </span>
                            </div>
                          </div>
                          <Button
                            data-testid={`remove-saved-path-${savedPath.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteSavedPath(savedPath.id)}
                          >
                            <Heart className="w-5 h-5 fill-red-500 text-red-500" />
                          </Button>
                        </div>
                        <Button
                          data-testid={`view-details-${savedPath.id}`}
                          variant="outline"
                          onClick={() => navigate(`/results/${savedPath.assessment_id}`)}
                        >
                          View Full Details
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Favorite FAQs Tab */}
              {activeTab === 'favorites' && (
                <div className="grid gap-4">
                  {favoriteFAQs.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-2xl">
                      <Bookmark className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-600 mb-4">No favorite FAQs yet</p>
                      <Button
                        data-testid="browse-faqs-btn"
                        onClick={() => navigate('/faqs')}
                        className="btn-primary"
                      >
                        Ask O4S.AI
                      </Button>
                    </div>
                  ) : (
                    favoriteFAQs.map((fav, idx) => (
                      <div
                        key={fav.id}
                        data-testid={`favorite-faq-${idx}`}
                        className={`bg-white rounded-xl shadow-sm border-2 overflow-hidden transition-all ${
                          expandedFavFAQ === fav.faq_id
                            ? 'border-primary shadow-lg'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-center p-5">
                          <button
                            onClick={() => setExpandedFavFAQ(expandedFavFAQ === fav.faq_id ? null : fav.faq_id)}
                            className="flex-1 text-left"
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs bg-gradient-to-r from-indigo-100 to-purple-100 text-primary px-3 py-1 rounded-full font-semibold border border-indigo-200">
                                {fav.category}
                              </span>
                              {fav.metadata?.location && (
                                <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full font-medium border border-green-200">
                                  📍 {fav.metadata.location}
                                </span>
                              )}
                              <span className="text-xs text-slate-400">
                                Saved {new Date(fav.saved_at).toLocaleDateString('en-IN')}
                              </span>
                            </div>
                            <h4 className="text-base font-heading font-semibold text-slate-900">
                              {fav.question}
                            </h4>
                          </button>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                            <button
                              data-testid={`remove-fav-${idx}`}
                              onClick={() => handleRemoveFavorite(fav.faq_id)}
                              className="w-8 h-8 rounded-full bg-amber-100 text-amber-600 hover:bg-amber-200 flex items-center justify-center transition-all"
                              title="Remove from favorites"
                            >
                              <Bookmark className="w-4 h-4 fill-amber-500" />
                            </button>
                            <button
                              onClick={() => setExpandedFavFAQ(expandedFavFAQ === fav.faq_id ? null : fav.faq_id)}
                              className={`w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center transition-all ${
                                expandedFavFAQ === fav.faq_id ? 'rotate-180 text-primary' : 'text-slate-400'
                              }`}
                            >
                              <ChevronDown className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        {expandedFavFAQ === fav.faq_id && fav.answer && (
                          <div className="px-5 pb-5 bg-slate-50 border-t border-slate-200">
                            <div className="pt-4 space-y-3">
                              {fav.answer.split('\n').map((line, i) => {
                                line = line.trim();
                                if (!line) return null;
                                if (line.startsWith('**') && line.endsWith('**')) {
                                  return <h3 key={i} className="text-base font-bold text-primary mt-4 mb-2 first:mt-0">{line.replace(/\*\*/g, '')}</h3>;
                                }
                                if (/^\d+\./.test(line)) {
                                  const match = line.match(/^(\d+)\.\s*(.+)$/);
                                  if (match) return <div key={i} className="flex gap-2 ml-4 mb-1"><span className="font-bold text-primary flex-shrink-0">{match[1]}.</span><span className="text-slate-700 text-sm">{match[2]}</span></div>;
                                }
                                if (line.includes('Learn:') || line.includes('Outcome:')) return <div key={i} className="ml-10 text-xs text-slate-600 mb-1">{line}</div>;
                                if (line.startsWith('-') || line.startsWith('•')) return <div key={i} className="flex gap-2 ml-4 mb-1"><span className="text-primary">•</span><span className="text-slate-700 text-sm">{line.substring(1).trim()}</span></div>;
                                return <p key={i} className="text-slate-700 text-sm leading-relaxed">{line}</p>;
                              })}
                            </div>
                            <div className="mt-4 pt-3 border-t border-slate-200">
                              <Button
                                data-testid={`view-faq-full-${idx}`}
                                variant="outline"
                                size="sm"
                                onClick={() => navigate('/faqs')}
                                className="gap-2"
                              >
                                <ExternalLink className="w-4 h-4" /> View in FAQ Browser
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;